import { useState, useEffect } from "react";
import { Startgroup } from "./api/Dtos";
import { PUT } from "./api/BackendApi";
import { useGlobalContext } from "./Layout/GlobalState";
import {
  ChakraProvider,
  HStack,
  Box,
  Text,
  IconButton,
  background,
} from "@chakra-ui/react";
import { useStartgroupsFromBackend } from "./Services/StartgroupService";
import {
  timeInMsToString,
  timeInMsDiffToString,
  dateTimeInMsToString,
} from "./Services/ResultService";
import { CircleStopIcon, PlayCircleIcon, Trash2Icon } from "lucide-react";
import { PromptForConfirmationModal } from "./Modals/PromptForConfirmationModal";
import { useParticipantsFromBackend } from "./Services/ParticipantService";
import { buildStartgroupDisciplineLabels } from "./Services/StartgroupDisciplineLabels";
import { s } from "vite/dist/node/types.d-aGj9QkWt";
import { idText, transform } from "typescript";

export function SetStarttime() {
  const globalContext = useGlobalContext();
  const { startgroups, setStartgroups, loading } = useStartgroupsFromBackend();
  const { participants } = useParticipantsFromBackend();
  const [time, setTime] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(Date.now());
    }, 500);
    return () => clearInterval(interval);
  }, []);

  if (!globalContext.competition) return <div>Ingen tävling är vald!</div>;
  if (loading) return <div>Laddar startgrupper...</div>;
  if (startgroups.length === 0) return <div>Inga startgrupper hittades.</div>;

  const disciplineLabelsByStartgroupId = buildStartgroupDisciplineLabels(
    globalContext.competition.disciplines,
    participants
  );

  return (
    <div>
      <HStack
        padding={"10px"}
        spacing={4}
        justify={"space-evenly"}
        marginBottom={"30px"}
      >
        <ChakraProvider>
          <Box width="100%" fontSize="48px">
            Tid: {timeInMsToString(time)}
          </Box>
        </ChakraProvider>
      </HStack>

      <Text fontSize="24px">Startgrupper: Starta klockor</Text>
      <Box
        width="100%"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid black" }}>ID</th>
              <th style={{ border: "1px solid black" }}>Planerad start</th>
              <th style={{ border: "1px solid black" }}>Start</th>
              <th style={{ border: "1px solid black" }}>Klar</th>
              <th style={{ border: "1px solid black" }}>Klocka</th>
              <th style={{ border: "1px solid black" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {startgroups.map((item: Startgroup) => {
              return (
                <TableRow
                  disciplineNames={
                    disciplineLabelsByStartgroupId[item.id] ?? []
                  }
                  currentTimeInMs={time}
                  onUpdateStartgroup={(update) => {
                    PUT.startgroup(update, globalContext.competition!.id).then(
                      () => {
                        setStartgroups((oldArray) =>
                          oldArray.map((entry) =>
                            entry.id === update.id ? update : entry
                          )
                        );
                      }
                    );
                  }}
                  startgroup={item}
                  key={`row_${item.id}`}
                />
              );
            })}
          </tbody>
        </table>
      </Box>
    </div>
  );
}

function TableRow(props: {
  disciplineNames: Array<string>;
  startgroup: Startgroup;
  currentTimeInMs: number;
  onUpdateStartgroup: (update: Startgroup) => void;
}) {
  const [startTime, setStartTime] = useState("-");
  const [stopTime, setStopTime] = useState("-");
  const [plannedStartTime, setPlannedStartTime] = useState("-");
  const [plannedStopTime, setPlannedStopTime] = useState("-");
  const [elapsedTime, setElapsedTime] = useState("-");

  useEffect(() => {
    setStartTime(timeInMsToString(props.startgroup.starttime_actual || null));
    setStopTime(timeInMsToString(props.startgroup.closetime_actual || null));
    setPlannedStartTime(
      timeInMsToString(props.startgroup.starttime_planned || null)
    );
    setPlannedStopTime(
      timeInMsToString(props.startgroup.closetime_planned || null)
    );
    setElapsedTime("-");
  }, [props.startgroup]);

  useEffect(() => {
    const hasStarted =
      props.startgroup.starttime_actual != null &&
      props.startgroup.starttime_actual != 0;
    const isRunning =
      hasStarted &&
      (props.startgroup.closetime_actual == null ||
        props.startgroup.closetime_actual == 0);

    if (!hasStarted) {
      setElapsedTime("Ej startad");
    } else if (isRunning) {
      setElapsedTime(
        timeInMsDiffToString(
          props.currentTimeInMs,
          truncateToSecond(props.startgroup.starttime_actual!)
        )
      );
    } else if (elapsedTime === "-") {
      setElapsedTime(
        timeInMsDiffToString(
          truncateToSecond(props.startgroup.closetime_actual!),
          truncateToSecond(props.startgroup.starttime_actual!)
        )
      );
    }
  }, [props.currentTimeInMs, elapsedTime]);

  const cellStyle = {
    border: "1px solid black",
    padding: "4px",
    background: props.startgroup.closetime_actual
      ? "#bbf7d0"
      : props.startgroup.starttime_actual
      ? "#fef08a"
      : undefined,
  };
  return (
    <tr key={props.startgroup.id}>
      <td style={cellStyle}>
        <div>
          <Text> {props.startgroup.id} </Text>
          <Text fontSize={"10px"}>{props.disciplineNames.join(", ")}</Text>
        </div>
      </td>
      <td
        style={cellStyle}
        title={dateTimeInMsToString(props.startgroup.starttime_planned)}
      >
        {plannedStartTime}
      </td>
      <td
        style={cellStyle}
        title={dateTimeInMsToString(props.startgroup.starttime_actual)}
      >
        {startTime}
      </td>
      <td
        style={cellStyle}
        title={dateTimeInMsToString(props.startgroup.closetime_actual)}
      >
        <Text
          textColor={props.startgroup.closetime_actual ? undefined : "gray.400"}
        >
          {props.startgroup.closetime_actual
            ? stopTime
            : `(approx: ${plannedStopTime})`}
        </Text>
      </td>
      <td style={cellStyle}>
        <Text fontSize="32px">{elapsedTime}</Text>
      </td>
      <td style={{ ...cellStyle, textAlign: "left" }}>
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fit, minmax(30px, 1fr))"
          gap={1}
        >
          <VerifyClickButton
            icon={<PlayCircleIcon />}
            disable={
              props.startgroup.starttime_actual != null &&
              props.startgroup.starttime_actual != 0 &&
              (props.startgroup.closetime_actual == null ||
                props.startgroup.closetime_actual == 0)
            }
            verifyIfAndOnlyIf={
              props.startgroup.starttime_actual != null &&
              props.startgroup.starttime_actual != 0
            }
            verifyQuestion={"Vill du starta klockan igen?"}
            onVerifiedClick={() => {
              if (
                props.startgroup.starttime_actual == null ||
                props.startgroup.starttime_actual == 0
              ) {
                props.onUpdateStartgroup({
                  ...props.startgroup,
                  starttime_actual: new Date().getTime(),
                });
              } else if (
                props.startgroup.closetime_actual != null &&
                props.startgroup.closetime_actual != 0
              ) {
                props.onUpdateStartgroup({
                  ...props.startgroup,
                  closetime_actual: null,
                });
              }
            }}
          />
          <VerifyClickButton
            icon={<CircleStopIcon />}
            disable={
              props.startgroup.starttime_actual == null ||
              props.startgroup.starttime_actual == 0 ||
              (props.startgroup.closetime_actual != null &&
                props.startgroup.closetime_actual != 0)
            }
            verifyIfAndOnlyIf={false}
            verifyQuestion={"Är du säker på att du vill sätta en ny stopptid?"}
            onVerifiedClick={() => {
              props.onUpdateStartgroup({
                ...props.startgroup,
                closetime_actual: new Date().getTime(),
              });
            }}
          />
          <VerifyClickButton
            icon={<Trash2Icon color={"red"} />}
            disable={
              props.startgroup.starttime_actual == null ||
              props.startgroup.starttime_actual == 0 ||
              props.startgroup.closetime_actual == null ||
              props.startgroup.closetime_actual == 0
            }
            verifyIfAndOnlyIf={true}
            verifyQuestion={"Återställ, vill du radera start och stop tid?"}
            onVerifiedClick={() => {
              props.onUpdateStartgroup({
                ...props.startgroup,
                starttime_actual: null,
                closetime_actual: null,
              });
            }}
          />
        </Box>
      </td>
    </tr>
  );
}

function VerifyClickButton(props: {
  verifyQuestion: string;
  verifyIfAndOnlyIf: boolean;
  disable: boolean;
  onVerifiedClick: () => void;
  icon:
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | undefined;
}) {
  const [showPrompt, setShowPrompt] = useState(false);
  const hoverBehaviour = props.disable
    ? undefined
    : { transform: "scale(1.2)" };
  return (
    <div>
      <IconButton
        variant={"ghost"}
        color={"black"}
        transition="transform 0.2s ease-in-out"
        _hover={hoverBehaviour}
        aria-label={props.verifyQuestion}
        icon={props.icon}
        isDisabled={props.disable}
        onClick={() => {
          if (props.disable) {
            return;
          } else if (props.verifyIfAndOnlyIf) {
            setShowPrompt(true);
          } else {
            props.onVerifiedClick();
          }
        }}
      />
      {showPrompt && (
        <PromptForConfirmationModal
          colorScheme="red"
          onConfirm={props.onVerifiedClick}
          onClose={() => {
            setShowPrompt(false);
          }}
          request={props.verifyQuestion}
        />
      )}
    </div>
  );
}

function truncateToSecond(timeInMs: number) {
  return Math.trunc(timeInMs / 1000) * 1000;
}

export default SetStarttime;
